import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import {
  IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED,
  USE_RECORDING_V2,
} from '@/components/constants/flags';
import { useSplitScreen } from '@/container/ScribeTabs/hooks/useSplitScreen';
import { isGenerateConsultNoteFromDocumentTemplateEnabled } from '@/features/flags';
import { isChunkedLiveDictation } from '@/features/flags/live-dictate-transcript-flag';
import {
  addStartNowStoppedSessionId,
  removeStartNowStoppedSessionId,
  startNowStoppedSessionIdsAtom,
} from '@/features/sessions/atoms/start-now-stopped-session-ids-atom';
import useMutateCreateDocumentFromNote from '@/hooks/mutation/useMutateCreateDocumentFromNote';
import {
  invalidateLiveTranscript,
  setIsPollingChunks,
  setSessionChunkUploadProgress,
} from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useCurrentSessionIsTabSharing } from '@/hooks/useCurrentSessionIsTabSharing';
import { useSessionRecording } from '@/lib/SessionRecordingProvider/useSessionRecording';
import {
  sessionPauseRecording,
  sessionResumeRecording,
  sessionStartRecording,
  sessionStopRecording,
} from '@/lib/analytics/scribe/session';
import {
  appendRecordingAttemptCheckpoint,
  finishRecordingAttempt,
  type RecordingAttemptTracking,
  type RecordingStartOptions,
} from '@/lib/telemetry/recordingStartTracking';
import {
  createPostCaptureSetupError,
  createSessionFlowPreflightError,
} from '@/lib/telemetry/sessionFlowFailures';
import { type Dispatch, store } from '@/store';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { isTranscribeLikeMode } from '@/utils/transcribeMode';
import { sanitizeError } from '@heidi/runtime/logger';
import {
  cancelTracking,
  finishTracking,
  getActiveTrackings,
  TrackingKey,
} from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  GenerationMethod,
  RecordingStatus,
  TemplateCategory,
} from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { useQuerySessionById } from '@heidi/session/data';
import { isAxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMutateUpdateSessionDuration } from '../mutation/useMutateUpdateSessionDuration';
import { useMutateUpdateSessionRecordingStatus } from '../mutation/useMutateUpdateSessionRecordingStatus';
import { TabType, useSessionWindows } from '../query/useQuerySessionWindows';
import { useCheckIsBlockedOnConsent } from './useCheckIsBlockedOnConsent';
import { useTutorialActions } from './useTutorialActions';

type StartRecording = (tracking?: RecordingAttemptTracking) => Promise<void>;

export const useRecordingV2 = () => {
  const sessionId = useCurrentSessionId();
  const { start, stop } = useSessionRecording();
  const dispatch = useDispatch<Dispatch>();
  const checkIsBlockedOnConsent = useCheckIsBlockedOnConsent(sessionId);
  const { mutate: updateRecordingStatus } = useMutateUpdateSessionRecordingStatus();
  const tutorial = useTutorialActions();
  const { splitScreen } = useSplitScreen();
  const { ensureTabFocus } = useSessionWindows({ sessionId });
  const setReadyToRequestMicPerms = useSetAtom(readyToRequestMicPermissionsAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setStoppedSessionIds = useSetAtom(startNowStoppedSessionIdsAtom);
  const isTabSharingSession = useCurrentSessionIsTabSharing();

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        consult_note: data.consult_note,
      };
    },
  });
  const { data: template } = useQueryTemplateById(
    session?.consult_note.template_id,
    session?.consult_note.generation_method
  );
  const { mutate: createEmptyDocumentFromNote } = useMutateCreateDocumentFromNote({
    sessionId,
  });
  const { mutate: updateSessionDuration } = useMutateUpdateSessionDuration();

  const freezeTimerAndSeedDuration = useCallback(() => {
    const duration = Number(store.getState().timerModel.seconds);

    // `useUpdateTimerFromSession` makes cached session duration authoritative as
    // soon as the timer stops running. Seed that cache first so the transition
    // from live ticks to persisted duration is atomic and cannot briefly restore
    // zero (first stop) or the previous pause duration (resume then stop).
    reactQueryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (old) => (old ? { ...old, duration } : old)
    );
    dispatch.timerModel.onPause();

    return duration;
  }, [dispatch.timerModel, sessionId]);

  const startRecording: StartRecording = useCallback(
    async (requestedTracking) => {
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: sessionId },
      ]);

      // Determine resume-vs-start BEFORE start() — once start() runs and the
      // timer ticks, `seconds` would also be > 0 on a fresh start.
      const isResume = store.getState().timerModel.seconds > 0;
      const fallbackTrackingKey = isResume
        ? TrackingKey.SCRIBE_RESUME_RECORDING
        : TrackingKey.SCRIBE_START_RECORDING;
      // Capture behaviour still comes from recorder/timer state. Telemetry uses
      // the click-time operation and instance so an async state change cannot
      // close a different operation or a newer retry.
      const tracking =
        requestedTracking ??
        ({
          trackingKey: fallbackTrackingKey,
          trackingInstanceId: getActiveTrackings().get(fallbackTrackingKey)?.instanceId,
        } satisfies RecordingAttemptTracking);

      if (!session?.transcribe_mode) {
        // A missing mode is a failed client attempt before capture starts.
        finishRecordingAttempt(tracking, {
          outcome: 'failed',
          error: createSessionFlowPreflightError(
            'StartRecordingFailed',
            'Session has no transcription mode'
          ),
        });
        // Same leak as the catch below: the button set the atom optimistically,
        // so this early exit must clear it or the navigation guard stays armed.
        setActiveSessionId(null);
        return;
      }

      let audioStarted = false;
      try {
        await start(isTabSharingSession);
        audioStarted = true;
        appendRecordingAttemptCheckpoint(tracking, 'audio_started');
        splitScreen({
          isTranscriptTabPending: isChunkedLiveDictation(session.transcribe_mode),
        });
        appendRecordingAttemptCheckpoint(tracking, 'split_screen');
        dispatch.timerModel.onStart(session?.duration ?? 0);
        appendRecordingAttemptCheckpoint(tracking, 'timer_started');
        finishRecordingAttempt(tracking);

        const seconds = store.getState().timerModel.seconds;
        if (seconds > 0) {
          // Resume recording
          // SCR-3481: force a fresh fetch of the live-transcript query so the
          // backend returns the populated user_uploaded[] that was previously
          // only surfaced on a hard page refresh.
          void invalidateLiveTranscript(sessionId);
          sessionResumeRecording({
            sessionId: sessionId,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });
        } else {
          // Start recording
          const selectedDevice = store.getState().audio.selectedAudioSource;
          sessionStartRecording({
            sessionId: sessionId,
            transcribeMode: session?.transcribe_mode,
            device: selectedDevice?.label,
            deviceId: selectedDevice?.deviceId,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });
        }
      } catch (error) {
        // The capture package can close a failed start first. Finishing here
        // also covers failures before its start lifecycle begins.
        if (!audioStarted) {
          finishRecordingAttempt(tracking, { outcome: 'failed', error });
        } else {
          // Capture is live, but the idle reset below makes the start fail for
          // the clinician. After the terminal action above, this is a no-op.
          finishRecordingAttempt(tracking, {
            outcome: 'failed',
            error: createPostCaptureSetupError(),
          });
        }
        logger.error({
          event_type: 'START_ERROR_V2',
          details: {
            errorName: error instanceof Error ? error.constructor.name : typeof error,
            // Covers late failures too, which the metric can no longer record.
            audioStarted,
          },
        });
        updateRecordingStatus({ sessionId, recordingStatus: RecordingStatus.IDLE });
        // The record button sets activeSessionIdAtom optimistically before this
        // async start runs. When start() rejects (e.g. the tab-sharing prompt is
        // cancelled), clear it too — otherwise the navigation guard keeps blocking
        // session switches with no recording in progress.
        setActiveSessionId(null);
      }
    },
    [
      sessionId,
      start,
      isTabSharingSession,
      splitScreen,
      dispatch,
      updateRecordingStatus,
      setActiveSessionId,
    ]
  );

  const onStartButtonPressed = useCallback(
    (props?: RecordingStartOptions) => {
      setReadyToRequestMicPerms(true);
      if (!props?.shouldBypassConsentCheck && checkIsBlockedOnConsent()) return;
      tutorial.onStart();
      void startRecording(props?.tracking);
    },
    [setReadyToRequestMicPerms, checkIsBlockedOnConsent, tutorial, startRecording]
  );

  const onStopButtonPressed = useCallback(
    (isPaused?: boolean) => {
      const trackingKey = isPaused
        ? TrackingKey.SCRIBE_PAUSE_RECORDING
        : TrackingKey.SCRIBE_STOP_RECORDING;
      const fn = async () => {
        if (!sessionId) {
          // No session: drop the attempt.
          cancelTracking({ key: trackingKey });
          return;
        }

        if (!isPaused) {
          setStoppedSessionIds((current) =>
            addStartNowStoppedSessionId(current, sessionId)
          );
        }

        let audioStopped = false;
        try {
          const session = reactQueryClient.getQueryData<QuerySessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId: sessionId },
          ]);
          await stop();
          audioStopped = true;
          // The attempt ends at capture stop; the work below is not part of it.
          if (isPaused) {
            finishTracking({ key: trackingKey });
          } else {
            const recordedSeconds = Math.round(
              Number(store.getState().timerModel.seconds) || 0
            );
            finishTracking({
              key: trackingKey,
              ...(recordedSeconds > 0
                ? { metadata: { recordingDurationS: recordedSeconds } }
                : {}),
            });
          }
          if (!isPaused) {
            // List Stop follows this path via pendingRecordingAction, not the
            // header button. Clear the live-recording atom so the list does not
            // keep showing Stop after capture has already ended.
            setActiveSessionId(null);
          }
          void setIsPollingChunks(sessionId);
          // On-prem: seed session chunkUploadProgress before pausing the timer so
          // live transcript status/Loading share one source of truth while the
          // final chunk drains (note-gen will overwrite/advance this next).
          if (isOnPrem) {
            setSessionChunkUploadProgress(sessionId, 0);
          }
          // Only ending a recording should switch to Note while it generates.
          // A pause must preserve the clinician's current tab in split view.
          if (!isPaused) {
            ensureTabFocus(TabType.NOTE);
          }
          const duration = freezeTimerAndSeedDuration();
          tutorial.onStop();

          if (isPaused) {
            sessionPauseRecording({
              sessionId: sessionId,
              isRecordingV2Enabled: USE_RECORDING_V2(),
              isTabSharing: isTabSharingSession,
            });
          }

          if (!isPaused) {
            sessionStopRecording({
              sessionId: sessionId,
              duration,
              isRecordingV2Enabled: USE_RECORDING_V2(),
              isTabSharing: isTabSharingSession,
            });

            const shouldUseDocumentFlow =
              isTranscribeLikeMode(session?.transcribe_mode) &&
              ((!isGenerateConsultNoteFromDocumentTemplateEnabled() &&
                template?.template_category === TemplateCategory.DOCUMENT_TEMPLATE) ||
                (IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED() &&
                  template?.template_category === TemplateCategory.FORM_TEMPLATE));

            if (shouldUseDocumentFlow) {
              createEmptyDocumentFromNote({
                trigger: 'autonomous',
                generationMethod: GenerationMethod.TEMPLATE,
                templateCategory: template.template_category,
                templateId: template.id,
              });
            } else {
              dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
            }
          }

          updateSessionDuration(
            { sessionId, duration },
            {
              onError: () => {
                if (!isPaused) {
                  setStoppedSessionIds((current) =>
                    removeStartNowStoppedSessionId(current, sessionId)
                  );
                }
              },
            }
          );
        } catch (error) {
          // Only a throw before audio stopped fails the attempt.
          if (!audioStopped) {
            finishTracking({ key: trackingKey, outcome: 'failed', error });
          }
          if (!isPaused) {
            setStoppedSessionIds((current) =>
              removeStartNowStoppedSessionId(current, sessionId)
            );
          }
          logger.error({
            event_type: 'STOP_ERROR_V2',
            details: {
              errorName: error instanceof Error ? error.constructor.name : typeof error,
              // An axios rejection's `message` can embed the request URL/body.
              ...(isAxiosError(error) ? sanitizeError(error) : {}),
            },
          });
        }
      };

      void fn();
    },
    [
      sessionId,
      stop,
      ensureTabFocus,
      dispatch,
      freezeTimerAndSeedDuration,
      tutorial,
      updateSessionDuration,
      isTabSharingSession,
      template,
      createEmptyDocumentFromNote,
      setStoppedSessionIds,
      setActiveSessionId,
    ]
  );

  const onInterrupt = useCallback(() => {
    if (!sessionId) return;
    ensureTabFocus(TabType.NOTE);
    const duration = freezeTimerAndSeedDuration();
    tutorial.onStop();
    updateSessionDuration({ sessionId, duration });
  }, [
    sessionId,
    ensureTabFocus,
    freezeTimerAndSeedDuration,
    tutorial,
    updateSessionDuration,
  ]);

  return useMemo(
    () => ({
      stopRecording: onStopButtonPressed,
      startRecording: onStartButtonPressed,
      pauseRecording: () => onStopButtonPressed(true),
      onInterrupt,
    }),
    [onStopButtonPressed, onStartButtonPressed, onInterrupt]
  );
};
